/**
 * GalleryView — F12.E T12.E.3.
 *
 * Widok galerii rekordów modułu custom — kafelki z miniaturą obrazu/pliku.
 *
 * Dla każdego rekordu:
 *  - Miniatura: pierwsze pole image/file z wartością (lazy presigned URL)
 *  - Brak miniatury: kolorowy placeholder z inicjałem record_name
 *  - Tytuł: record_name
 *  - 2 pierwsze visible pola (bez pola cover) jako tekst poniżej
 *  - Menu: Edytuj / Usuń
 *
 * Konfigurowalne przez `coverFieldSlug` (opcjonalne — auto-detect first image field).
 */

import { useQuery } from '@tanstack/react-query';
import { Image as ImageIcon } from 'lucide-react';

import { FieldRegistry, type ModuleField } from '@/components/fields/FieldRegistry';
import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';

import type { ModuleRecord } from './record-types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  moduleId: string;
  records: ModuleRecord[];
  fields: ModuleField[];
  /** Slug pola image/file który ma być okładką. Jeśli null → auto-detect. */
  coverFieldSlug?: string | null;
  isLoading?: boolean;
  onRecordClick: (record: ModuleRecord) => void;
  onRecordDelete: (record: ModuleRecord) => void;
}

// ─── Cover Image ──────────────────────────────────────────────────────────────

interface CoverImageProps {
  moduleId: string;
  minioKey: string;
  alt: string;
  moduleColor: string;
  initials: string;
}

function CoverImage({ moduleId, minioKey, alt, moduleColor, initials }: CoverImageProps) {
  const presignedQuery = useQuery({
    queryKey: ['presigned-url', moduleId, minioKey],
    queryFn: async () => {
      const res = await api.get<{ url: string }>(`/modules/${moduleId}/upload/download`, {
        params: { key: minioKey },
      });
      return res.data.url;
    },
    staleTime: 55 * 60 * 1000, // 55 min (presigned URL TTL = 1h)
    enabled: !!minioKey,
  });

  if (presignedQuery.isLoading) {
    return (
      <div
        className="flex h-full w-full items-center justify-center"
        style={{ backgroundColor: `${moduleColor}15` }}
      >
        <Spinner />
      </div>
    );
  }

  if (presignedQuery.data) {
    return (
      <img
        src={presignedQuery.data}
        alt={alt}
        className="h-full w-full object-cover"
        loading="lazy"
        onError={(e) => {
          // Fallback na placeholder gdy URL wygasł lub obraz niedostępny
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }

  // Fallback — inicjały
  return (
    <div
      className="flex h-full w-full items-center justify-center"
      style={{ backgroundColor: `${moduleColor}15` }}
    >
      <span className="text-2xl font-bold" style={{ color: moduleColor }}>
        {initials}
      </span>
    </div>
  );
}

// ─── Placeholder (brak obrazu) ────────────────────────────────────────────────

function CoverPlaceholder({ moduleColor, initials }: { moduleColor: string; initials: string }) {
  return (
    <div
      className="flex h-full w-full items-center justify-center"
      style={{ backgroundColor: `${moduleColor}15` }}
    >
      {initials ? (
        <span className="text-2xl font-bold" style={{ color: moduleColor }}>
          {initials}
        </span>
      ) : (
        <ImageIcon className="h-8 w-8 text-text-muted opacity-40" />
      )}
    </div>
  );
}

// ─── Gallery Card ─────────────────────────────────────────────────────────────

interface GalleryCardProps {
  record: ModuleRecord;
  moduleId: string;
  moduleColor: string;
  coverFieldSlug: string | null;
  previewFields: ModuleField[];
  onRecordClick: (record: ModuleRecord) => void;
  onRecordDelete: (record: ModuleRecord) => void;
}

function GalleryCard({
  record,
  moduleId,
  moduleColor,
  coverFieldSlug,
  previewFields,
  onRecordClick,
  onRecordDelete,
}: GalleryCardProps) {
  // Jeśli record_name nie jest ustawiony — spróbuj wyciągnąć wartość pierwszego widocznego pola
  const fallbackName = !record.record_name
    ? (previewFields
        .map((f) => (record as Record<string, unknown>)[f.slug])
        .find((v) => v != null && v !== '')
        ?.toString() ?? `#${record.id.slice(0, 8)}`)
    : null;
  const recordName = record.record_name ?? fallbackName ?? `#${record.id.slice(0, 8)}`;
  const initials = (recordName[0] ?? '?').toUpperCase();

  const coverKey = coverFieldSlug ? (record as Record<string, unknown>)[coverFieldSlug] : null;
  const coverMinioKey = typeof coverKey === 'string' && coverKey ? coverKey : null;

  return (
    <div
      className="group relative flex cursor-pointer flex-col overflow-hidden rounded-xl border border-border-primary bg-bg-primary shadow-sm transition-shadow hover:shadow-md"
      onClick={() => onRecordClick(record)}
      data-testid={`gallery-card-${record.id}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onRecordClick(record);
      }}
    >
      {/* Miniatura */}
      <div className="h-40 w-full overflow-hidden bg-bg-secondary">
        {coverMinioKey ? (
          <CoverImage
            moduleId={moduleId}
            minioKey={coverMinioKey}
            alt={recordName}
            moduleColor={moduleColor}
            initials={initials}
          />
        ) : (
          <CoverPlaceholder moduleColor={moduleColor} initials={initials} />
        )}
      </div>

      {/* Treść karty */}
      <div className="flex-1 p-3">
        <p className="truncate text-sm font-semibold text-text-primary" title={recordName}>
          {recordName}
        </p>

        {/* 2 pierwsze pola podglądu */}
        {previewFields.slice(0, 2).map((field) => {
          const val = (record as Record<string, unknown>)[field.slug];
          if (val == null || val === '') return null;
          const cellValue = FieldRegistry.renderCell(field, val);
          if (!cellValue) return null;
          return (
            <p
              key={field.id}
              className="mt-0.5 truncate text-xs text-text-secondary"
              title={String(cellValue)}
            >
              <span className="text-text-muted">{field.name}: </span>
              {String(cellValue)}
            </p>
          );
        })}
      </div>

      {/* Hover overlay — usuń */}
      <button
        type="button"
        className="absolute right-2 top-2 hidden rounded-md bg-white/80 px-2 py-1 text-xs text-error-600 backdrop-blur-sm hover:bg-white group-hover:flex"
        onClick={(e) => {
          e.stopPropagation();
          onRecordDelete(record);
        }}
        aria-label="Usuń rekord"
      >
        Usuń
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function GalleryView({
  moduleId,
  records,
  fields,
  coverFieldSlug: coverFieldSlugProp,
  isLoading,
  onRecordClick,
  onRecordDelete,
}: Props) {
  // Auto-detect pierwszego pola image/file jako okładki
  const coverFieldSlug =
    coverFieldSlugProp !== undefined
      ? coverFieldSlugProp
      : (fields.find((f) => f.fieldType === 'image' || f.fieldType === 'file')?.slug ?? null);

  // Pola podglądu — visible, ale nie cover
  const previewFields = fields.filter((f) => f.showInList && f.slug !== coverFieldSlug).slice(0, 2);

  // Kolor modułu — pobieramy z pierwszego rekordu meta lub używamy accent
  const moduleColor = '#3B82F6'; // TODO: przekazać z modułu jeśli potrzebne

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-text-muted">Brak rekordów do wyświetlenia.</p>
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
      data-testid="gallery-view"
    >
      {records.map((record) => (
        <GalleryCard
          key={record.id}
          record={record}
          moduleId={moduleId}
          moduleColor={moduleColor}
          coverFieldSlug={coverFieldSlug}
          previewFields={previewFields}
          onRecordClick={onRecordClick}
          onRecordDelete={onRecordDelete}
        />
      ))}
    </div>
  );
}
